<template>
  <v-navigation-drawer
    v-model="drawer"
    :width="$vuetify.breakpoint.mobile ? 280 : 400"
    right
    fixed
    app
    class="drawer-right-sale"
    :class="
      defineStore.cart.length || defineStore.buffet_plan_lines.length
        ? 'drawer-left'
        : 'no-data'
    "
  >
    <template v-slot:prepend>
      <!-- Customer Info Bar (shown when customer is selected) -->
      <div v-if="selectedCustomer" class="drawer-customer-bar">
        <span class="drawer-customer-bar__icon">
          <v-icon size="16">fal fa-user</v-icon>
        </span>
        <div class="drawer-customer-bar__body">
          <p class="drawer-customer-bar__name" :title="customerDisplayName">
            {{ customerDisplayName }}
          </p>
          <div class="drawer-customer-bar__meta">
            <span
              v-if="selectedCustomer.phone || selectedCustomer.phone_no"
              class="drawer-customer-bar__meta-item"
            >
              <v-icon size="11">fal fa-phone</v-icon>
              {{ selectedCustomer.phone || selectedCustomer.phone_no }}
            </span>
            <span
              class="drawer-customer-bar__meta-item drawer-customer-bar__meta-item--primary"
            >
              <v-icon size="11">fal fa-star</v-icon>
              {{ selectedCustomer.point || 0 }} {{ $t("loyalty_points") }}
            </span>
            <span
              v-if="showStampLoyaltyUi"
              class="drawer-customer-bar__meta-item drawer-customer-bar__meta-item--stamp"
            >
              <v-icon size="11">fal fa-stamp</v-icon>
              <v-progress-circular
                v-if="stampStatusLoading"
                indeterminate
                size="12"
                width="2"
                color="amber darken-2"
                class="ml-1"
              />
              <template v-else-if="stampStatus">
                <template v-if="stampOverflowExtra > 0">
                  {{ stampStatus.stamps_required_to_redeem }}/{{
                    stampStatus.stamps_required_to_redeem
                  }}
                  <span class="ml-1">+{{ stampOverflowExtra }}</span>
                </template>
                <template v-else>
                  {{ stampStatus.stamp_count }}/{{
                    stampStatus.stamps_required_to_redeem
                  }}
                </template>
              </template>
            </span>
          </div>
        </div>
        <v-btn
          icon
          class="drawer-customer-bar__close"
          :aria-label="$t('a11y_remove_customer_from_sale')"
          @click="removeCustomer"
        >
          <v-icon size="14">fal fa-times</v-icon>
        </v-btn>
      </div>
      <div v-if="showStampLoyaltyUi && selectedCustomer" class="px-4 pb-3 pt-2">
        <v-btn
          block
          outlined
          color="amber darken-2"
          class="stamp-view-card-btn"
          min-height="48"
          :loading="stampStatusLoading"
          @click="openStampLoyaltyModal"
        >
          <v-icon left small color="amber darken-2">fal fa-stamp</v-icon>
          {{ $t("stamp_view_card") }}
        </v-btn>
      </div>

      <!-- Restaurant: Show table name when table is selected but no customer -->
      <div
        v-else-if="hasTables && selectedTableFromRoute && !selectedCustomer"
        class="drawer-sale-header px-4"
      >
        <div class="d-flex align-center">
          <span
            :style="{
              backgroundColor: currentTableStatusColor,
            }"
            class="drawer-table-avatar"
          >
            <v-icon color="white" size="18">fal fa-utensils</v-icon>
          </span>
          <div class="flex-grow-1 drawer-sale-header__grow">
            <div class="d-flex align-center mb-1 flex-wrap">
              <span
                class="text-subtitle-1 font-weight-bold mr-2 text-truncate table-header-name"
                :title="selectedTableFromRoute.name"
              >
                {{ selectedTableFromRoute.name }}
              </span>
              <v-chip
                :color="currentTableStatusColor"
                text-color="white"
                x-small
                label
              >
                <v-icon left x-small>{{ currentTableStatusIcon }}</v-icon>
                {{ currentTableStatusText }}
              </v-chip>
            </div>
            <div class="text-caption grey--text">
              {{ $t("current_table") }}
              <span v-if="currentTableZone" class="ml-1">
                • {{ currentTableZone }}
              </span>
            </div>
          </div>
          <v-btn
            icon
            class="drawer-header-icon-btn ml-2 flex-shrink-0"
            :aria-label="$t('a11y_clear_table_selection')"
            @click="clearTableSelection"
          >
            <v-icon small>fal fa-times</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Cart Header: item count + Held Bills entry point (when no customer/table header is shown). Held Bills must stay reachable even with an empty cart, since resuming a held bill is how the cart gets filled. -->
      <div
        v-else-if="!selectedCustomer && !(hasTables && selectedTableFromRoute)"
        class="drawer-sale-header px-4"
      >
        <div class="d-flex align-center" style="width: 100%">
          <span class="drawer-cart-header__icon-badge" aria-hidden="true">
            <v-icon size="16">fal fa-shopping-cart</v-icon>
          </span>
          <div class="drawer-cart-header__text">
            <p class="drawer-cart-header__title">{{ $t("order_list") }}</p>
            <p class="drawer-cart-header__sub">
              {{ defineStore.cart.length }} {{ $t("items") }} ·
              {{ totalItemsCount }} {{ $t("units") }}
            </p>
          </div>
          <v-spacer />
          <div class="d-flex align-center flex-shrink-0">
            <span
              v-if="heldBills.length"
              class="drawer-cart-header__held-pill"
              :title="$t('held_bills')"
              @click="openHeldBills"
            >
              <v-icon size="12">fal fa-clock</v-icon>
              {{ heldBills.length }}
            </span>
            <v-btn
              v-else
              icon
              class="drawer-cart-header__icon-btn"
              :aria-label="$t('held_bills')"
              :title="$t('held_bills')"
              @click="openHeldBills"
            >
              <v-icon size="16">fal fa-clock</v-icon>
            </v-btn>
            <span
              v-if="defineStore.cart.length > 0"
              class="drawer-cart-header__clear ml-3"
              :aria-label="$t('clear_cart')"
              @click="clearCart"
              >{{ $t("clear") }}</span
            >
          </div>
        </div>
      </div>

      <!-- Price Channel Selector in Cart (shown when price channels exist) -->
      <div
        v-if="priceChannels && priceChannels.length > 0"
        class="drawer-channel-bar px-4 py-2 d-flex align-center justify-space-between"
      >
        <span class="text-caption grey--text d-flex align-center font-weight-medium">
          <v-icon x-small color="grey" class="mr-1">fal fa-tag</v-icon>
          {{ $t("price_channel") }}
        </span>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              small
              outlined
              v-bind="attrs"
              v-on="on"
              class="primary--text font-weight-bold"
              style="cursor: pointer;"
            >
              <v-icon v-if="activeChannel && activeChannel.icon" left x-small>{{ activeChannel.icon }}</v-icon>
              {{ activeChannelName }}
              <v-icon right x-small>fal fa-chevron-down</v-icon>
            </v-chip>
          </template>
          <v-list dense>
            <v-list-item @click="selectPriceChannel(null)">
              <v-list-item-title :class="{ 'primary--text font-weight-bold': !defineStore.selectedPriceChannelId }">
                {{ $t("default_price") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              v-for="ch in priceChannels"
              :key="ch.id"
              @click="selectPriceChannel(ch.id)"
            >
              <v-list-item-title :class="{ 'primary--text font-weight-bold': defineStore.selectedPriceChannelId === ch.id }">
                <v-icon v-if="ch.icon" left x-small>{{ ch.icon }}</v-icon>
                {{ ch.name }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
    <!-- no product widget -->
    <app-no-product
      v-if="
        defineStore.cart.length == 0 && !defineStore.buffet_plan_lines.length
      "
    />
    <app-cart-list
      v-else
      :picking-stamp-reward="pickingStampReward"
      @dialogQTY="dialogQTY"
      @pickStampReward="onPickStampReward"
      @removeStampReward="onRemoveStampReward"
    />
    <template v-slot:append>
      <!-- Note Section -->
      <div v-if="note && defineStore.cart.length > 0" class="drawer-note-bar">
        <v-icon size="15" class="drawer-note-bar__icon"
          >fal fa-sticky-note</v-icon
        >
        <div class="drawer-note-bar__body">
          <p class="drawer-note-bar__label">{{ $t("noted") }}</p>
          <p class="drawer-note-bar__text">{{ note }}</p>
        </div>
        <v-btn
          icon
          x-small
          class="drawer-note-bar__clear"
          :aria-label="$t('clear')"
          @click="note = ''"
        >
          <v-icon size="14" color="#94a3b8">fal fa-times</v-icon>
        </v-btn>
      </div>

      <div class="drawer-footer pa-3">
        <!-- 4 Action Buttons Row (PosSmart v2: Hold / Note / Discount / Customer) -->
        <ActionButtonsPOS
          v-if="store_type === 'POS'"
          :note-active="!!note"
          :customer-active="!!selectedCustomer"
          :discount-active="!!defineStore.total_discount"
          @hold="pauseOrder"
          @note="openNoted"
          @discount="openDiscout"
          @customer="makeCustomer"
        />
        <ActionButtonsRestaurant
          v-else
          @clear="clearCart"
          @tables="navigateToTables"
          @bill="navigateToBill"
          @products="navigateToProducts"
        />

        <!-- Totals Display Section (PosSmart v2 style) -->
        <div class="drawer-totals mt-3">
          <div class="drawer-totals__row">
            <span class="drawer-totals__label">{{ $t("cart_subtotal") }}</span>
            <span class="drawer-totals__value">
              {{ currencySymbolText
              }}{{ defineStore.total_price | NumberFormat }}
            </span>
          </div>
          <div class="drawer-totals__row drawer-totals__row--discount">
            <span class="drawer-totals__label">{{ $t("discount") }}</span>
            <span class="drawer-totals__value">
              -{{ currencySymbolText
              }}{{ defineStore.total_discount | NumberFormat }}
            </span>
          </div>
          <div class="drawer-totals__row">
            <span class="drawer-totals__label"
              >{{ $t("vat") }} ({{ vatRate }}%)</span
            >
            <span class="drawer-totals__value">
              {{ currencySymbolText }}{{ vatAmount | NumberFormat }}
            </span>
          </div>
          <div class="drawer-totals__divider"></div>
          <div class="drawer-totals__grand">
            <span class="drawer-totals__grand-label">{{
              $t("cart_grand_total")
            }}</span>
            <span class="drawer-totals__grand-value">
              {{ currencySymbolText }}{{ grandTotalWithVat | NumberFormat }}
            </span>
          </div>
        </div>

        <v-alert
          v-if="pickingStampReward"
          type="info"
          dense
          outlined
          class="mt-3 stamp-redeem-alert"
        >
          <div class="d-flex align-center">
            <v-icon left small color="amber darken-2">fal fa-stamp</v-icon>
            <span class="text-body-2">
              {{ $t("stamp_pick_free_item_hint") }}
            </span>
            <v-spacer />
            <v-btn text small class="ml-2" @click="pickingStampReward = false">
              {{ $t("cancel") }}
            </v-btn>
          </div>
        </v-alert>

        <v-alert
          v-else-if="redeemStampPending"
          :type="stampRedeemValidation.valid ? 'success' : 'warning'"
          dense
          outlined
          class="mt-3 stamp-redeem-alert"
        >
          {{ stampRedeemValidationText }}
        </v-alert>

        <!-- Payment Button - POS (PosSmart v2 brutal brand) -->
        <v-btn
          v-if="store_type === 'POS'"
          @click="dialogPayOpen"
          color="primary"
          x-large
          depressed
          block
          class="drawer-pay-btn mt-3"
          :disabled="!canMakePayment || !canProcessSales"
        >
          <v-icon size="16" left>fal fa-credit-card</v-icon>
          <span class="drawer-pay-btn__label">{{ $t("payment") }}</span>
        </v-btn>

        <!-- Restaurant: Table/Order Button -->
        <v-btn
          v-else
          @click="handleRestaurantAction"
          color="primary"
          x-large
          depressed
          block
          class="drawer-pay-btn mt-3"
          :disabled="!canMakeRestaurantAction || !canProcessSales"
          :loading="isAddingOrder"
        >
          <v-icon left size="18">{{ tableActionIcon }}</v-icon>
          <span class="drawer-pay-btn__label">{{ tableActionText }}</span>
        </v-btn>

        <!-- Keep quotation button if needed -->
        <app-right-action
          v-if="new_quatation"
          @addQuotation="addQuotation"
          @fetchTable="fetchTable"
          @handleTableClick="handleTableClick"
          :new_quatation="new_quatation"
          :store_type="store_type"
          class="mt-2"
        />
      </div>
    </template>

    <!-- Payment -->
    <app-custom-dialog
      v-model="dialogPay"
      :footer="false"
      :title="actionHandler"
      :width="actionHandler === 'customer' ? '650px' : '500px'"
      :body-padding-top="false"
    >
      <div slot="activator">
        <!-- back fab icon button -->
        <v-btn
          icon
          @click="actionHandler = 'payment'"
          v-if="actionHandler !== 'payment'"
        >
          <v-icon>fal fa-arrow-left</v-icon>
        </v-btn>
        <!-- payment -->
        <!-- Mount only once the payment view is actually shown, then keep it (v-show) so the selected tab survives the exchange-rate/customer views -->
        <UnifiedPaymentPad
          v-if="actionHandler === 'payment' || paymentPadSeen"
          v-show="actionHandler === 'payment'"
          @hook:mounted="paymentPadSeen = true"
          :total-amount="finalTotalPrice"
          :display-amount="fullTotalPrice"
          :store-type="'POS'"
          :active-currencies="activeCurrencies"
          :debug="true"
          :is-processing="isPaymentProcessing"
          @submit="makeSubmit($event)"
          @submitPayment="handleSubmitPayment"
          @manualPaymentSuccess="handleManualPaymentSuccess"
          @currency-change="onCurrencyChange"
          @switchToCash="handleSwitchToCash"
          @customer="makeCustomer"
          @openNoted="openNoted"
          @exchangeRate="exchangeRate"
          @paymentAmountUpdated="handlePaymentAmountUpdated"
          ref="saleNumPad"
        />
        <!-- customer -->
        <template v-if="actionHandler === 'customer'">
          <AppCustomer
            clean
            @selectCustomer="onCustomerSelectedInPaymentDialog"
          />
        </template>
        <!-- exchange rate -->
        <template v-if="actionHandler === 'exchangeRate'">
          <NumericExchangeRate
            :totalPrice="finalTotalPrice"
            :currencies="activeCurrencies"
            :productCurrency="actualProductCurrency"
            :debug="true"
            @submit="submitExchangeRate"
            @currency-change="onCurrencyChange"
          />
        </template>
      </div>
    </app-custom-dialog>

    <!-- Hidden card used for image generation -->
    <div style="position: fixed; left: -99999px; top: -99999px">
      <PaymentQRCard
        v-if="qrCodeData && qrCodeData.qrCode"
        ref="qrCard"
        :store-name="getStoreName()"
        :amount="qrCodeData.amount"
        :ticket-id="qrCodeData.ticket_id"
        :qr-image="qrCodeData.qrCode"
        :merchant-name="getStoreName()"
        :merchant-id="user?.stores?.merchant_id || '000000000000000'"
      />
    </div>

    <!-- Receipt print -->
    <app-bill v-show="print" :item="printBill" />
    <!-- quotation print -->
    <app-print v-if="show_quotation" v-show="print" :item="printBill" />
    <!-- app table -->
    <app-custom-dialog
      width="1000px"
      v-model="chooseTable"
      @submit="confirmTableOrder()"
      title="choose_table"
    >
      <div slot="activator">
        <app-choose-table
          :tables="tables"
          :table_id="table_id"
          :order_id="order_id"
          @setTable="setTable"
          @mergeTable="mergeTable"
        />
      </div>
    </app-custom-dialog>
    <app-loading :overlay="overlay" />

    <!-- Note Dialog -->
    <app-custom-dialog
      v-model="noteDialog"
      title="save"
      subtitle="note_for_this_bill"
      width="480px"
      cancel-text="cancel"
      submit-text="save"
      delete-text="delete"
      @delete="clearNoteDraft"
      @submit="saveNoteDraft"
    >
      <div slot="activator">
        <v-textarea
          v-model="noteDraft"
          :placeholder="$t('note_placeholder')"
          outlined
          rows="5"
          maxlength="500"
          counter="500"
          hide-details="auto"
          class="note-dialog-textarea"
        />
      </div>
    </app-custom-dialog>

    <!-- Clear cart confirmation -->
    <app-custom-dialog
      v-model="clearCartDialog"
      title="clear_cart"
      width="420px"
      cancel-text="cancel"
      submit-text="confirm"
      @submit="confirmClearCart"
    >
      <div slot="activator" class="clear-cart-confirmation">
        {{ $t("are_you_sure_clear_cart") }}
      </div>
    </app-custom-dialog>

    <!-- Discount Dialog -->
    <app-custom-dialog
      v-model="dialog"
      title="discount_option"
      width="420"
      :footer="false"
    >
      <div slot="activator">
        <DiscountModal
          ref="discountModalRef"
          :total-before-discount="totalBeforeDiscount"
          :discount-type="typeDiscount"
          :initial-amount="amount"
          :currency-symbol="defineStore?.currencySymbol?.symbol ?? '₭'"
          :value="dialog"
          @apply="handleDiscountApply"
          @close="onNumpadClose"
        />
      </div>
    </app-custom-dialog>

    <!-- Held Bills Dialog -->
    <app-custom-dialog
      v-model="heldOpen"
      :title="$t('held_bills')"
      :subtitle="$t('held_bills_count', { count: heldBills.length })"
      :is_translate="false"
      width="480"
      :footer="false"
    >
      <div slot="activator">

        <div v-if="heldBills.length" class="ps-held-dialog__list thin-scroll">
          <div
            v-for="bill in heldBills"
            :key="bill.txn"
            class="ps-held-dialog__item"
          >
            <div class="ps-held-dialog__item-head">
              <p class="ps-held-dialog__item-label">
                {{ bill.txn }}
                <span class="ps-held-dialog__item-time">{{
                  heldTime(bill.created_at)
                }}</span>
              </p>
              <p class="ps-held-dialog__item-total">
                {{ heldBillTotal(bill) | NumberFormat }}
                {{ heldBillSymbol(bill) }}
              </p>
            </div>
            <div class="ps-held-dialog__item-meta">
              <span class="ps-held-dialog__item-count">
                {{ bill.orders_count }} {{ $t("items") }}
              </span>
              <span
                v-if="bill.customer"
                class="ps-held-dialog__item-chip ps-held-dialog__item-chip--brand"
              >
                <v-icon size="10">fal fa-user</v-icon>
                {{ customerNameOf(bill.customer) }}
              </span>
            </div>
            <div class="ps-held-dialog__item-actions">
              <button
                class="ps-held-dialog__btn ps-held-dialog__btn--brand"
                @click="resumeBill(bill)"
              >
                {{ $t("resume") }}
              </button>
              <button
                class="ps-held-dialog__btn ps-held-dialog__btn--danger"
                @click="deleteHeldBill(bill)"
              >
                {{ $t("delete") }}
              </button>
            </div>
          </div>
        </div>
        <div v-else class="ps-held-dialog__empty">
          <span class="ps-held-dialog__empty-icon"
            ><v-icon size="26">fal fa-clock</v-icon></span
          >
          <p class="ps-held-dialog__empty-title">
            {{ $t("held_bills_empty") }}
          </p>
        </div>
      </div>
    </app-custom-dialog>
    <!-- Stamp loyalty card (retail POS) -->
    <StampLoyaltyModal
      v-model="stampLoyaltyDialog"
      :customer-id="customer_id"
      :stamp-status="stampStatus"
      :loading="stampStatusLoading"
      :apply-loading="stampModalApplyLoading"
      :apply-disabled="isPaymentProcessing"
      :redeem-validation="stampRedeemValidation"
      :applied="redeemStampPending"
      :cart-empty="defineStore.cart.length === 0"
      @apply-reward="onStampModalApplyReward"
      @remove-reward="onRemoveStampReward"
    />
    <!-- Clock In Dialog (required before sales) -->
    <ClockInDialog
      v-model="showClockInDialog"
      @success="handleClockInSuccess"
    />
  </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import AppNoProduct from "@/components/products/no_product";
import AppQR from "@/components/qr";
import AppCartList from "@/components/products/cart_list";
import AppHeader from "@/components/console/header";
import { myMixin } from "@/mixin/main";
import AppBill from "@/components/print/bill";
import {
  toast,
  confirm,
  successPos,
  successCompleteSale,
  shouldPrintFromPaymentAlert,
} from "@/controllers/alert";
import NumericKeyboard from "@/components/NumericKeyboard";
import AppChooseTable from "@/components/restaurant/chooseTable";
import { mainStore } from "@/stores/modules/cart";
import AppPrint from "@/components/documents/print";
import AppTitle from "@/components/settings/app_title";
import NumericPad from "@/components/NumericPad";
import AppRightAction from "@/components/layouts/btn_action";
import QRCode from "qrcode";
import PubNub from "pubnub";
import { pubnubConfig } from "@/utils/mock";
import AppCustomer from "@/components/customers/CustomerSelector.vue";
import NumericExchangeRate from "@/components/NumericExchangeRate.vue";
import PaymentQRCard from "@/components/print/PaymentQRCard.vue";
import SaleNumPad from "@/components/sale/SaleNumPad.vue";
import UnifiedPaymentPad from "@/components/payment/UnifiedPaymentPad.vue";
import ActionButtonsPOS from "@/components/layouts/ActionButtonsPOS.vue";
import ActionButtonsRestaurant from "@/components/layouts/ActionButtonsRestaurant.vue";
import DiscountModal from "@/components/layouts/DiscountModal.vue";
import ClockInDialog from "@/pages/shift-management/ClockInDialog";
import StampLoyaltyModal from "@/components/sale/StampLoyaltyModal.vue";

export default {
  components: {
    AppNoProduct,
    AppQR,
    AppCartList,
    AppBill,
    AppHeader,
    NumericKeyboard,
    AppChooseTable,
    AppPrint,
    AppTitle,
    NumericPad,
    AppRightAction,
    AppCustomer,
    NumericExchangeRate,
    PaymentQRCard,
    SaleNumPad,
    UnifiedPaymentPad,
    ActionButtonsPOS,
    ActionButtonsRestaurant,
    DiscountModal,
    ClockInDialog,
    StampLoyaltyModal,
  },
  mixins: [myMixin],
  props: {
    debug: { type: Boolean, default: false },
  },
  data() {
    return {
      number: "",
      defineStore: mainStore(),
      table_id: "",
      order_id: "",
      chooseTable: false,
      show_quotation: false,
      print: false,
      dialog: false,
      dialogPay: false,
      typeDiscount: "percentage",
      amount: "0",
      pay_amount: "",
      status_payment: "done",
      right: true,
      drawer: false,
      printBill: {},
      last_amount: 0,
      order_table: {},
      note: "",
      noteDialog: false,
      noteDraft: "",
      clearCartDialog: false,
      windowListener: this.handleKeydown,
      // Held bills (Pause/Hold orders saved via make_cart_order)
      heldBills: [],
      heldOpen: false,
      heldLoading: false,
      dialogQ: false,
      amountQty: "0",
      cartItem: {
        qty: 0,
        item: {},
      },
      pubnub: new PubNub(pubnubConfig()),
      selectedCurrency: null,
      selectedCurrencyData: null,
      // Payment tabs and QR data
      qrLoading: false,
      qrCodeData: {},
      currentTicketId: null,
      manualPaymentLoading: false,
      paymentTab: "cash",
      showManualPaymentDialog: false,
      manualPaymentReference: "",
      manualPaymentErrors: [],
      actionHandler: "payment",
      // Store converted amount from exchange rate
      pendingConvertedAmount: null,
      pendingCurrencyData: null,
      paymentPadSeen: false,
      // Payment processing state to prevent duplicate submissions
      isPaymentProcessing: false,
      // Store payment details from UnifiedPaymentPad for use in success alert
      lastPaymentDetails: null,
      // Number keys
      numberKeys: ["7", "8", "9", "4", "5", "6", "1", "2", "3", "C", "0", "."],
      // Discount modal validation error
      discountValidationError: null,
      // Shift clock-in is required before allowing sales/payment
      showClockInDialog: false,
      stampStatus: null,
      redeemStampPending: false,
      stampLoyaltyDialog: false,
      stampStatusLoading: false,
      stampModalApplyLoading: false,
      pickingStampReward: false,
      isAddingOrder: false,
    };
  },
  watch: {
    selectedTableFromRoute: {
      immediate: true,
      handler(t) {
        if (!this.hasTables) return;

        if (t?.id) {
          // Arriving on /sale with a table selected (Add Order from /table page).
          // The cart must start empty so new items only ever flow as additions to the
          // existing ticket. Existing history and buffet plans live on the server and
          // are never re-sent from this drawer.
          this.order_table = {};
          this.defineStore.deleteCart();
        } else if (this.isBuffet) {
          // No table selected: drop leftover buffet plan lines so totals do not show phantom plan sums.
          this.order_table = {};
          this.defineStore.setBuffetPlanLines([]);
        }
      },
    },
    customer_id: {
      handler(newId, oldId) {
        this.redeemStampPending = false;
        this.pickingStampReward = false;
        if (newId !== oldId) {
          this.stampStatus = null;
        }
        this.refreshStampStatus();
      },
      immediate: true,
    },
    "defineStore.cart": {
      handler(newCart) {
        if (!newCart || newCart.length === 0) {
          this.pickingStampReward = false;
          this.redeemStampPending = false;
          return;
        }
        if (this.redeemStampPending && !this.stampRedeemValidation.valid) {
          this.redeemStampPending = false;
        }
      },
      deep: true,
    },
    dialog: {
      handler(isOpen, wasOpen) {
        // When dialog closes (transitions from open to closed) and amount is set, apply the discount
        // This ensures we only apply discount when user submits, not when opening the dialog
        if (wasOpen && !isOpen && this.amount && this.amount !== "0") {
          this.applyDiscount(this.amount);
        }
      },
      immediate: false,
    },
    actionHandler: {
      handler(newHandler, oldHandler) {
        // When switching to payment view and we have pending conversion data
        if (
          newHandler === "payment" &&
          oldHandler === "exchangeRate" &&
          this.pendingConvertedAmount &&
          this.pendingCurrencyData
        ) {
          // Use the more reliable retry mechanism
          this.$nextTick(() => {
            this.trySetExternalCurrency(this.pendingCurrencyData, 0);
            this.trySetExternalPaymentAmount(this.pendingConvertedAmount, 0);
          });
        }
      },
      immediate: false,
    },
  },
  created() {
    if (!this.$vuetify.breakpoint.smAndDown) {
      this.drawer = true;
    }
    // Keep the held-bills badge in the cart header accurate.
    this.$nextTick(() => this.fetchHeldBills());
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      discount_type: "index/discount_type",
      customers: "index/customers",
      tables: "index/tables",
      overlay: "index/overlay",
      newQuotation: "index/newQuotation",
      activeCurrencies: "index/activeCurrencies",
      customer_id: "index/customer_id",
      tax_rate: "auth/tax_rate",
      tax_include: "auth/tax_include",
      activeShift: "index/activeShift",
      store_type: "auth/store_type",
    }),

    canProcessSales() {
      const storeData = this.user?.stores || {};
      const requireShiftEnabled =
        storeData.require_shift === 1 || storeData.require_shift === true;
      if (!requireShiftEnabled) return true;
      return !!this.activeShift;
    },

    vatEnabled() {
      return !!this.user?.stores?.tax_include;
    },
    vatRate() {
      // Use Vuex getter with default 10% tax rate
      return Number(this.tax_rate || 10);
    },
    vatAmount() {
      const { total_price, total_discount } = this.defineStore;
      const taxable = Math.max(
        Number(total_price || 0) - Number(total_discount || 0),
        0,
      );
      if (!this.vatEnabled || this.vatRate <= 0) return 0;
      return (taxable * this.vatRate) / 100;
    },
    grandTotalWithVat() {
      const { total_price, total_discount } = this.defineStore;
      const taxable = Math.max(
        Number(total_price || 0) - Number(total_discount || 0),
        0,
      );
      return taxable + this.vatAmount;
    },

    currencySymbolText() {
      return this.defineStore?.currencySymbol?.symbol ?? "₭";
    },

    // Total before discount for discount modal
    totalBeforeDiscount() {
      const { total_price, total_discount } = this.defineStore;
      return Math.max(
        Number(total_price || 0) - Number(total_discount || 0),
        0,
      );
    },

    new_quatation() {
      // check if newQuotation is not empty and contain the customer_id in newQuotation then return true
      if (this.newQuotation && this.newQuotation.customer_id) {
        this.show_quotation = true;
        return true;
      }
      return false;
    },

    // Calculate final total price after discount with proper currency handling
    finalTotalPrice() {
      const { total_price, total_discount, cart } = this.defineStore;
      const finalPrice = this.grandTotalWithVat;

      // Check if cart has items with different currencies
      const hasNonLAKItems = cart.some(
        (item) => item.currency && item.currency.code !== "LAK",
      );

      return finalPrice;
    },
    // Full total price before discount (for display in Amount Due)
    priceChannels() {
      return this.defineStore.priceChannels || [];
    },

    activeChannel() {
      return this.defineStore.selectedPriceChannel;
    },

    activeChannelName() {
      return (
        this.defineStore.activePriceChannelName || this.$t("default_price")
      );
    },

    fullTotalPrice() {
      const { total_price, cart } = this.defineStore;
      return total_price;
    },

    // Get the actual product currency from cart items
    actualProductCurrency() {
      const { cart } = this.defineStore;
      if (!cart || cart.length === 0) {
        return { code: "LAK", symbol: "₭" };
      }

      // Get the currency from the first cart item (assuming all items have the same currency)
      const firstItem = cart[0];
      if (firstItem && firstItem.currency) {
        return {
          code: firstItem.currency.code,
          symbol:
            firstItem.currency.symbol ||
            (firstItem.currency.code === "THB" ? "฿" : "₭"),
        };
      }

      // Fallback to LAK if no currency found
      return { code: "LAK", symbol: "₭" };
    },

    // Numpad title for discount dialog
    numpadTitle() {
      return this.typeDiscount === "percentage"
        ? "discount_percentage"
        : "discount_amount";
    },

    showTableButton() {
      return this.$route.query.tableId || this.defineStore.cart.length > 0;
    },

    // New computed properties from POC
    totalItemsCount() {
      return this.defineStore.cart.reduce((sum, item) => sum + item.qty, 0);
    },

    canMakePayment() {
      return this.defineStore.cart.length > 0;
    },

    paymentButtonColor() {
      return this.canMakePayment ? "primary" : "grey";
    },

    // Get selected customer from customers list
    selectedCustomer() {
      if (this.customer_id && this.customers && this.customers.length > 0) {
        return this.customers.find((c) => c.id === this.customer_id);
      }
      return null;
    },

    customerDisplayName() {
      const c = this.selectedCustomer;
      if (!c) {
        return "";
      }
      if (c.name) {
        return c.name;
      }
      if (c.en_name) {
        return c.en_name;
      }
      if (c.la_name) {
        return c.la_name;
      }
      const first = c.first_name || "";
      const last = c.last_name || "";
      return `${first} ${last}`.trim() || "";
    },

    /**
     * Retail POS: show stamp entry when the feature exists for this store type.
     * Only show when stamp program is enabled in store settings.
     */
    showStampLoyaltyUi() {
      if (this.store_type !== "POS" || !this.selectedCustomer) {
        return false;
      }
      if (this.stampStatusLoading) {
        return false;
      }
      return !!(
        this.stampStatus &&
        this.stampStatus.stamp_feature_available &&
        this.stampStatus.is_active
      );
    },

    /**
     * Number of stamps the customer is holding above the redeem threshold.
     * Used by the header chip so a customer with e.g. 20/10 displays as
     * "10/10 +10" instead of the visually misleading "20/10".
     */
    stampOverflowExtra() {
      if (!this.stampStatus) return 0;
      const required = Number(this.stampStatus.stamps_required_to_redeem) || 0;
      const count = Number(this.stampStatus.stamp_count) || 0;
      return Math.max(0, count - required);
    },

    stampRewardLines() {
      return this.defineStore.cart.filter((item) => {
        const customPrice =
          item.customPrice !== undefined && item.customPrice !== null
            ? Number(item.customPrice)
            : null;
        return customPrice === 0 || Number(item.price || 0) <= 0;
      });
    },

    stampRewardLineCount() {
      return this.stampRewardLines.length;
    },

    stampRedeemValidation() {
      if (!this.selectedCustomer) {
        return { valid: false, key: "stamp_redeem_requires_customer" };
      }
      if (!this.stampStatus) {
        return { valid: false, key: "stamp_no_status" };
      }
      if (!this.stampStatus.stamp_feature_available) {
        return { valid: false, key: "stamp_retail_only" };
      }
      if (!this.stampStatus.is_active) {
        return { valid: false, key: "stamp_program_inactive" };
      }
      if (!this.stampStatus.eligible) {
        return { valid: false, key: "stamp_not_enough_to_redeem" };
      }
      if (this.stampRewardLineCount === 0) {
        return { valid: false, key: "stamp_reward_line_missing" };
      }
      if (this.stampRewardLineCount > 1) {
        return {
          valid: false,
          key: "stamp_redeem_single_free_line_only",
        };
      }
      const rewardQty = Number(this.stampRewardLines[0]?.qty || 0);
      if (rewardQty !== 1) {
        return { valid: false, key: "stamp_redeem_reward_qty_one_only" };
      }
      return { valid: true, key: "stamp_reward_ready" };
    },

    stampRedeemValidationText() {
      return this.$t(this.stampRedeemValidation.key);
    },

    // Get table from route query (Restaurant mode)
    selectedTableFromRoute() {
      const tableId = this.$route.query.tableId;
      const tableName = this.$route.query.tableName;
      return tableId
        ? { id: tableId, name: tableName || `Table ${tableId}` }
        : null;
    },

    // Get current table details from tables list
    currentTableDetails() {
      if (!this.selectedTableFromRoute || !this.tables) return null;
      return this.tables.find(
        (table) => table.id === this.selectedTableFromRoute.id,
      );
    },

    // Current table status color
    currentTableStatusColor() {
      if (!this.currentTableDetails) return "primary";
      return this.tableStatusColor(
        this.currentTableDetails.status || "available",
      );
    },

    // Current table status icon
    currentTableStatusIcon() {
      if (!this.currentTableDetails) return "fal fa-utensils";
      return this.iconStatus(this.currentTableDetails.status || "available");
    },

    // Current table status text
    currentTableStatusText() {
      if (!this.currentTableDetails) return this.$t("available");
      const status = this.currentTableDetails.status || "available";
      return this.$t(status) || status;
    },

    // Current table zone
    currentTableZone() {
      if (!this.currentTableDetails || !this.currentTableDetails.zone)
        return null;
      return this.currentTableDetails.zone.name || null;
    },

    // Restaurant / buffet table action button text
    tableActionText() {
      if (!this.hasTables) return "";
      return this.selectedTableFromRoute
        ? this.$t("add_order")
        : this.$t("choose_table");
    },

    tableActionIcon() {
      if (!this.hasTables) return "";
      return this.selectedTableFromRoute
        ? "fal fa-plus-circle"
        : "fal fa-utensils";
    },

    tableActionColor() {
      if (!this.hasTables) return "primary";
      if (this.$vuetify.theme.dark) return "primary";
      // Align "Add Order" button color with Payment button color
      return "primary";
    },

    canMakeRestaurantAction() {
      if (!this.hasTables) return false;
      return this.selectedTableFromRoute || this.defineStore.cart.length > 0;
    },
  },
  methods: {
    selectPriceChannel(channelId) {
      this.defineStore.setPriceChannel(channelId);
      if (this.defineStore.cart.length > 0) {
        toast(this, "channel_price_updated");
      }
    },
    dlog(label, payload) {
      if (this.debug) {
        try {
          // Debug logging disabled
        } catch (_) {}
      }
    },
    ...mapMutations({
      SET_PAYMENT: "index/SET_PAYMENT",
      SET_CUSTOMER: "index/SET_CUSTOMER",
      SET_TABLES: "index/SET_TABLES",
      SET_OVERLAY: "index/SET_OVERLAY",
      SET_NEW_QUOTATION: "index/SET_NEW_QUOTATION",
      SET_CUSTOMER_ID: "index/SET_CUSTOMER_ID",
    }),

    ...mapActions({
      fetch: "index/fetch",
      fetchActiveCurrencies: "index/fetchActiveCurrencies",
      fetchActiveShift: "index/fetchActiveShift",
    }),

    async handleClockInSuccess() {
      await this.fetchActiveShift(this);
      this.showClockInDialog = false;
    },

    ensureCanProcessSales() {
      if (this.canProcessSales) return true;
      toast(this, "clock_in_required", "error");
      this.showClockInDialog = true;
      return false;
    },

    /**
     * @param {number|string|null|undefined} explicitCustomerId - When set (e.g. from payment modal pick), fetch even if watcher skipped same-id updates.
     */
    async refreshStampStatus(explicitCustomerId) {
      const targetId =
        explicitCustomerId != null ? explicitCustomerId : this.customer_id;
      if (!targetId) {
        this.stampStatus = null;
        this.stampStatusLoading = false;
        return;
      }
      this.stampStatusLoading = true;
      try {
        const { data } = await this.$http.get(
          `customers/${targetId}/stamp_status`,
        );
        if (data && data.status) {
          this.stampStatus = data.data;
        } else {
          this.stampStatus = null;
        }
      } catch (e) {
        this.stampStatus = null;
      } finally {
        this.stampStatusLoading = false;
      }
    },

    openStampLoyaltyModal() {
      this.stampLoyaltyDialog = true;
      this.refreshStampStatus();
    },

    async onStampModalApplyReward() {
      this.stampModalApplyLoading = true;
      try {
        if (!this.selectedCustomer) {
          toast(this, "stamp_redeem_requires_customer", "error");
          return;
        }
        if (!this.stampStatus || !this.stampStatus.eligible) {
          toast(this, "stamp_not_enough_to_redeem", "error");
          return;
        }
        if (!this.defineStore.cart || this.defineStore.cart.length === 0) {
          toast(this, "stamp_pick_no_items", "error");
          return;
        }
        this.stampLoyaltyDialog = false;
        this.pickingStampReward = true;
        toast(this, "stamp_pick_free_item_hint", "info");
      } finally {
        this.stampModalApplyLoading = false;
      }
    },

    onPickStampReward(index) {
      if (!this.pickingStampReward) return;
      this.defineStore.markCartLineAsStampReward(index);
      this.redeemStampPending = true;
      this.pickingStampReward = false;
      toast(this, "stamp_reward_added", "success");
    },

    onRemoveStampReward() {
      this.defineStore.unmarkCartLineAsStampReward();
      this.redeemStampPending = false;
      this.pickingStampReward = false;
      toast(this, "stamp_remove_reward_done", "success");
    },

    markStampRedeemReady() {
      if (!this.stampRedeemValidation.valid) {
        toast(this, this.stampRedeemValidation.key, "error");
        return false;
      }
      this.redeemStampPending = true;
      toast(this, "stamp_redeem_ready_toast", "success");
      return true;
    },

    async fetchItem() {
      const body = {
        that: this,
        url: "customers",
        commit: "SET_CUSTOMER",
      };
      await this.fetch(body);
    },
    cancelDiscount() {
      if (this.defineStore.cart.length > 0) {
        var total = this.defineStore.cart
          .map((e) => e.price)
          .reduce((a, b) => a + b);
        this.defineStore.setTotalPrice(total);
        this.amount = 0;
      }
      this.dialog = false;
    },

    removeNumber() {
      const myString = this.pay_amount;
      if (myString.length - 1 != 0) {
        var newStr = myString.substring(0, myString.length - 1);
        this.pay_amount = newStr;
      } else {
        this.pay_amount = "";
      }
    },
    async dialogPayOpen() {
      if (!this.ensureCanProcessSales()) return;
      // Fetch active currencies before opening payment dialog
      // This ensures currencies are available when UnifiedPaymentPad mounts
      if (!this.activeCurrencies || this.activeCurrencies.length === 0) {
        await this.fetchActiveCurrencies(this);
      }

      // Detect cart currency and set it automatically
      // This ensures the payment interface displays the price in the original currency (e.g., THB)
      const { cart } = this.defineStore;
      if (cart && cart.length > 0) {
        const firstItem = cart[0];
        if (firstItem && firstItem.currency && firstItem.currency.code) {
          const cartCurrencyCode = firstItem.currency.code;

          // Find matching currency in activeCurrencies
          const matchingCurrency = this.activeCurrencies.find(
            (c) => c.code === cartCurrencyCode,
          );

          if (matchingCurrency) {
            // Set currency in store so UnifiedPaymentPad uses it
            this.defineStore.setCurrency(matchingCurrency);
          } else if (cartCurrencyCode !== "LAK") {
            // If cart has non-LAK currency but not found in activeCurrencies,
            // create a temporary currency object from cart item
            const cartCurrency = {
              id: firstItem.currency.id || null,
              code: cartCurrencyCode,
              name: firstItem.currency.name || cartCurrencyCode,
              symbol: firstItem.currency.symbol || "₭",
              store_exchange_rate:
                firstItem.exchange_rate?.toString() || "1.00",
            };
            this.defineStore.setCurrency(cartCurrency);
          } else {
            // Cart is in LAK, ensure currency is cleared or set to LAK
            const lakCurrency = this.activeCurrencies.find(
              (c) => c.code === "LAK",
            );
            if (lakCurrency) {
              this.defineStore.setCurrency(lakCurrency);
            } else {
              // Clear currency if LAK not found (will use fallback)
              this.defineStore.setCurrency(null);
            }
          }
        }
      }

      this.dialogPay = true;
      const { total_price, total_discount } = this.defineStore;
      const total = this.grandTotalWithVat;
      this.pay_amount = total.toString();
      this.defineStore.setReceived(total);

      // Clear QR data and reset payment status when opening payment dialog
      this.qrCodeData = null;
      this.qrLoading = false;
      this.manualPaymentReference = "";
      this.manualPaymentErrors = [];
      this.showManualPaymentDialog = false;

      // Clear any pending conversion data
      this.pendingConvertedAmount = null;
      this.pendingCurrencyData = null;

      // Reset SaleNumPad component state to ensure fresh start
      if (this.$refs.saleNumPad) {
        this.$refs.saleNumPad.reset();
        this.$refs.saleNumPad.resetQR();
      }
    },

    // Method to remove table selection
    removeTableSelection() {
      this.$router.push({
        query: {
          ...this.$route.query,
          // Remove tableId to allow user to select a new table
          tableId: null,
          tableName: null,
        },
      });
    },

    // Method to handle table click
    handleTableClick() {
      // Check if we're in table selection mode
      if (this.$route.query.tableId && this.defineStore.cart.length > 0) {
        // If a table is already selected and cart has items, confirm the order
        this.confirmTableOrder();
      } else if (
        this.$route.query.tableId &&
        this.defineStore.cart.length === 0
      ) {
        // If table selected but no items in cart, allow changing table
        this.removeTableSelection();
      } else {
        // No table selected, show table selection modal
        this.fetchTable();
      }
    },

    async fetchTable() {
      // Check if we already have a selected table from the tables page
      if (this.$route.query.tableId) {
        this.table_id = this.$route.query.tableId;
        // Clear any existing order_table data for new order
        this.order_table = {};
        // Don't show the table selection modal
        return;
      }

      this.SET_OVERLAY(true);
      const body = {
        url: "store_table",
        that: this,
        commit: "SET_TABLES",
      };
      await this.fetch(body);
      this.SET_OVERLAY(false);
      this.chooseTable = true;
    },
    async submitPayment() {
      // Prevent duplicate submissions
      if (this.isPaymentProcessing) {
        return;
      }

      if (this.redeemStampPending && !this.stampRedeemValidation.valid) {
        toast(this, this.stampRedeemValidation.key, "error");
        return;
      }

      // Set loading state to prevent duplicate submissions
      this.isPaymentProcessing = true;

      try {
        const isQRActive = this.user.stores.is_active_qr;

        // Get actual paid amount from user input
        const paidAmount =
          this.lastPaymentDetails?.paidAmount ||
          parseFloat(this.pay_amount) ||
          this.defineStore.received;

        const body = {
          that: this,
          customer_id: this.customer_id,
          typeDiscount: this.typeDiscount,
          status: this.status_payment,
          note: this.note,
          type: this.paymentTab,
          isQRActive,
          paidAmount: paidAmount,
          redeem_stamp_reward: this.redeemStampPending,
          // Include full split payment payload for cash+transfer (cash_transfer)
          paymentDetails: this.lastPaymentDetails || null,
        };

        const result = await this.defineStore.saleProduct(body);

        if (result && result.status) {
          this.printBill = result.data;
          if (this.paymentTab === "qr" && isQRActive) {
            this.qrLoading = true;

            // Check if qr_code exists and is not empty
            if (!result.data.qr_code || result.data.qr_code.trim() === "") {
              toast(this, "qr_generation_failed", "error");
              this.qrLoading = false;
              this.resetPayment();
              return;
            }

            try {
              const qrCode = await QRCode.toDataURL(result.data.qr_code);
              this.qrCodeData = {
                qrCode,
                ticket_id: result.data.ticket_id,
                amount: result.data.amount,
                terminal_label:
                  result.data.terminal_label + " - " + result.data.amount,
              };

              // Update SaleNumPad component
              if (this.$refs.saleNumPad) {
                this.$refs.saleNumPad.setQRData(this.qrCodeData);
              }

              // Update customer screen via Pinia store
              this.defineStore.setCustomerScreenQRData(this.qrCodeData);

              await this.listenTicket(result.data.ticket_id);
              this.qrLoading = false;
            } catch (error) {
              toast(this, "qr_generation_failed", "error");
              this.qrLoading = false;
              this.resetPayment();
              return;
            }
          } else {
            // Handle non-QR successful payments
            // Close payment dialog FIRST before showing success modal to prevent modal stacking
            this.dialogPay = false;

            // Wait for dialog to close before showing success modal
            await this.$nextTick();

            // Small delay to ensure dialog is fully closed
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Calculate cash back (change) if paid amount exceeds total price
            // Use paidAmount from UnifiedPaymentPad which reflects what user actually entered
            // Fallback to backend total_paid if user input is not available
            const paidAmount =
              this.lastPaymentDetails?.paidAmount ||
              parseFloat(this.pay_amount) ||
              result.data.total_paid ||
              0;
            const totalPrice = result.data.total_price || 0;
            const totalDiscount = result.data.total_discount || 0;

            // Determine product currency and payment currency from API response
            let productCurrency = "LAK"; // Default to LAK
            if (result.data.history && result.data.history.length > 0) {
              const firstItem = result.data.history[0];
              if (firstItem.product && firstItem.product.currency) {
                productCurrency = firstItem.product.currency.code || "LAK";
              }
            }

            const paymentCurrency = result.data.currency_id?.code || "LAK"; // Payment currency
            const exchangeRate = parseFloat(result.data.exchange_rate) || 1;

            // Convert totalPrice to payment currency if currencies differ
            let convertedTotalPrice = totalPrice;
            if (productCurrency !== paymentCurrency && exchangeRate > 0) {
              // If product is in foreign currency (THB/USD) and payment is LAK
              // totalPrice is already in product currency, need to convert to payment currency
              if (productCurrency !== "LAK" && paymentCurrency === "LAK") {
                // Product currency → LAK: multiply by exchange rate
                convertedTotalPrice = totalPrice * exchangeRate;
              } else if (
                productCurrency === "LAK" &&
                paymentCurrency !== "LAK"
              ) {
                // LAK → Payment currency: divide by exchange rate
                convertedTotalPrice = totalPrice / exchangeRate;
              }
            }

            // Note: total_price from backend already has discount subtracted
            // Backend calculates: total_price = (total_price_sale - total_discount) + tax_amount
            // So we should use convertedTotalPrice directly without subtracting totalDiscount again
            const finalAmount = convertedTotalPrice;
            const change =
              paidAmount > finalAmount ? paidAmount - finalAmount : 0;

            // Add change to result data for display in success alert
            const saleDataWithChange = {
              ...result.data,
              change: change,
            };

            // Show success dialog, then reset state and optionally print
            const alertResult = await successCompleteSale(
              this,
              saleDataWithChange,
            );

            // Reset cart and payment state
            this.defineStore.resetCart(this);
            this.resetPayment(); // Reset local component state including discount
            this.qrCodeData = null;
            this.status_payment = "done";
            this.SET_PAYMENT();

            // Reset pay_amount
            this.pay_amount = "0";

            // Prepare receipt data and print if confirmed
            if (result.data) {
              this.printBill = result.data;
              if (shouldPrintFromPaymentAlert(alertResult)) {
                // Ensure component is still mounted before executing
                if (this.$el && this.$el.parentNode) {
                  setTimeout(() => {
                    if (this.$el && this.$el.parentNode) {
                      this.$htmlToPaper("printBill");
                    }
                  }, 500);
                }
              }
            }
          }
        } else {
          // Show error to user
          toast(this, "payment_failed", "error");
          this.resetPayment();
        }
      } catch (error) {
        toast(this, "payment_failed", "error");
        this.resetPayment();
      } finally {
        // Always clear loading state when done (success or error)
        this.isPaymentProcessing = false;
      }
    },

    async resetPayment() {
      this.status_payment = "done";
      this.SET_PAYMENT(1);
      this.pay_amount = "0";
      this.note = "";
      this.SET_CUSTOMER_ID(null);
      this.qrCodeData = null;
      this.qrLoading = false;
      this.manualPaymentReference = "";
      this.manualPaymentErrors = [];
      this.showManualPaymentDialog = false;
      this.actionHandler = "payment";
      this.defineStore.setReceived(0);

      // Reset discount-related state
      this.amount = "0";
      this.typeDiscount = "amount";
      this.dialog = false; // Close discount dialog if open

      // Reset customer screen QR state via Pinia store
      this.defineStore.resetCustomerScreenQR();
      this.defineStore.setCurrency(null);

      // Reset UnifiedPaymentPad component if available
      if (this.$refs.saleNumPad) {
        this.$refs.saleNumPad.reset();
        this.$refs.saleNumPad.resetQR();
      }

      // Clear any pending conversion data
      this.pendingConvertedAmount = null;
      this.pendingCurrencyData = null;
      this.redeemStampPending = false;
      this.pickingStampReward = false;
      this.stampStatus = null;
      this.stampLoyaltyDialog = false;
      this.stampStatusLoading = false;
      this.stampModalApplyLoading = false;

      // Safely close dialog
      if (this.dialogPay) {
        this.dialogPay = false;
      }
    },

    async listenTicket(ticket_id) {
      if (this._ticketPubNubListener) {
        try {
          this.pubnub.removeListener(this._ticketPubNubListener);
        } catch (error) {
          // Error handled silently
        }
        this._ticketPubNubListener = null;
      }

      if (this.currentTicketId) {
        const previousChannel = String(this.currentTicketId);
        try {
          this.pubnub.unsubscribe({ channels: [previousChannel] });
        } catch (error) {
          // Error handled silently
        }
      }

      this.currentTicketId = ticket_id;
      const channel = String(ticket_id);

      this._ticketPubNubListener = {
        message: (event) => {
          if (event.channel !== channel) {
            return;
          }
          if (event.message === "qr_payment_success") {
            this.handleQRPaymentSuccess(ticket_id);
          }
        },
      };
      this.pubnub.addListener(this._ticketPubNubListener);
      this.pubnub.subscribe({ channels: [channel] });
    },

    async handleQRPaymentSuccess(ticket_id) {
      this.defineStore.resetCart(this);
      const { data } = await this.$http.get(`bill_detail/${ticket_id}`);
      this.printBill = data.data;
      this.resetPayment();
      // Notify customer screen of payment success via Pinia store
      this.defineStore.setCustomerScreenPaymentSuccess(true);
      const alertResult = await successPos(this, data.data, true);
      if (shouldPrintFromPaymentAlert(alertResult)) {
        // Ensure component is still mounted before executing
        if (this.$el && this.$el.parentNode) {
          setTimeout(() => {
            if (this.$el && this.$el.parentNode) {
              this.$htmlToPaper("printBill");
            }
          }, 500);
        }
      }
    },

    /**
     * Laravel serializes buffetPlans relation as buffet_plans in JSON.
     * Alias so hydrateBuffetPiniaFromTicket can use buffetPlans regardless of payload key.
     */
    normalizeOrderTablePayload(raw) {
      if (!raw) return raw;
      if (raw.buffet_plans && !raw.buffetPlans) {
        this.$set(raw, "buffetPlans", raw.buffet_plans);
      }
      return raw;
    },

    async setTable(id) {
      try {
        this.table_id = id;
        this.order_table = {};
        const { data } = await this.$http.get(`get_table_order/${id}?check=0`);
        if (data && data.status && data.data) {
          const payload = this.normalizeOrderTablePayload(data.data);
          this.order_table = payload;
          this.hydrateBuffetPiniaFromTicket(payload);
        } else {
          this.order_table = {};
          this.hydrateBuffetPiniaFromTicket(null);
        }
      } catch (error) {
        // Error handled silently
      }
    },

    hydrateBuffetPiniaFromTicket(ticket) {
      if (!this.isBuffet) return;
      if (!ticket) {
        this.defineStore.setBuffetPlanLines([]);
        return;
      }
      const plans = ticket.buffetPlans || ticket.buffet_plans || [];
      if (!plans.length) {
        this.defineStore.setBuffetPlanLines([]);
        return;
      }
      const lines = plans.map((p) => {
        let includedIds = [];
        if (Array.isArray(p.included_category_ids)) {
          includedIds = p.included_category_ids
            .map((id) => Number(id))
            .filter((id) => id > 0);
        } else if (Array.isArray(p.categories)) {
          includedIds = p.categories
            .map((c) => Number(c.id))
            .filter((id) => id > 0);
        }
        return {
          buffet_plan_id: Number(p.buffet_plan_id ?? p.plan_id ?? p.id ?? 0),
          plan_name: String(p.plan_name || p.name || "").trim(),
          unit_price:
            Number(p.unit_price ?? p.price_per_person ?? p.price ?? 0) || 0,
          persons: Math.max(1, Number(p.persons ?? p.qty ?? 1) || 1),
          subtotal: Number(p.subtotal ?? p.total ?? 0) || 0,
          included_category_ids: includedIds,
        };
      });
      this.defineStore.setBuffetPlanLines(lines);
    },

    async confirmTableOrder() {
      if (!this.ensureCanProcessSales()) return;
      // Get table ID from URL query or local table_id
      const tableId = this.$route.query.tableId || this.table_id;

      // check table_id is empty
      if (!tableId || tableId === "") {
        return toast(this, "choose_table_required", "error");
      }
      // check cart is empty
      if (this.defineStore.cart.length === 0 || this.defineStore.cart === "") {
        return toast(this, "choose_product_required", "error");
      }
      this.isAddingOrder = true;
      try {
        await this.makeTableOrder();
      } finally {
        this.isAddingOrder = false;
      }
    },
    async mergeTable(id) {
      if (id === this.order_id) {
        this.order_id = "";
        return;
      }
      this.order_id = id;
    },

    async makeCustomer(fromDrawer = false) {
      this.actionHandler = "customer";
      this.status_payment = "done";
      this.dialogPay = true;
    },
    onCustomerSelectedInPaymentDialog(customerId) {
      this.actionHandler = "payment";
      this.refreshStampStatus(customerId ?? this.customer_id);
    },
    async makeSubmit(paymentDetails = null) {
      if (!this.ensureCanProcessSales()) return;
      const { total_price, total_discount, currency } = this.defineStore;
      const finalTotal = this.grandTotalWithVat;

      // Get actual paid amount from user input (paymentDetails from UnifiedPaymentPad)
      const paidAmount =
        paymentDetails?.paidAmount ||
        this.lastPaymentDetails?.paidAmount ||
        parseFloat(this.pay_amount) ||
        0;

      // Convert pay_amount to the same currency as total_price for comparison
      let payAmountInBaseCurrency = paidAmount;

      // If currency is selected and different from base currency, convert pay_amount back to base currency
      if (currency && currency.store_exchange_rate) {
        const exchangeRate = Number(currency.store_exchange_rate);

        if (exchangeRate > 0) {
          // Convert from selected currency back to base currency (LAK)
          payAmountInBaseCurrency = payAmountInBaseCurrency * exchangeRate;
        }
      }

      // Add tolerance for currency conversion rounding errors (5 LAK tolerance)
      const tolerance = 5;

      if (
        finalTotal > payAmountInBaseCurrency + tolerance &&
        total_discount === 0
      ) {
        const { isConfirmed } = await confirm({
          props: this,
          title: "not_enough_money",
          text: "ok_to_overdue",
        });
        if (isConfirmed) {
          await toast(this, "choose_customer", "info");
          this.status_payment = "overdue";
        }
      } else {
        // Update paymentTab with the actual payment method from UnifiedPaymentPad
        if (paymentDetails && paymentDetails.method) {
          this.paymentTab = paymentDetails.method;
        }

        // Store payment details for use in success alert (contains paidAmount and change)
        this.lastPaymentDetails = paymentDetails;

        this.submitPayment();
      }
    },

    async makeTableOrder() {
      try {
        const { arr, cart_id } = await Help.tableOrder(this.defineStore.cart);
        let status_order = "new";
        let ticket_id = null;

        // Check if we have a pre-selected table from the route query
        const tableId = this.$route.query.tableId || this.table_id;

        if (!tableId) {
          return toast(this, "choose_table_required", "error");
        }

        // Check if we have an existing order to merge with
        if (this.order_table && this.order_table.id) {
          status_order = "merge";
          ticket_id = this.order_table.id;
        } else {
          // Fallback: route-selected table may have an existing ticket (not hydrated at mount).
          try {
            await this.setTable(tableId);
            if (this.order_table && this.order_table.id) {
              status_order = "merge";
              ticket_id = this.order_table.id;
            }
          } catch (error) {}
        }

        if (this.isBuffet) {
          // /sale right drawer does not manage buffet plans. Strip any lines that
          // setTable may have hydrated so the request body has no `buffet_plans` key,
          // which keeps existing plans on the ticket untouched server-side.
          this.defineStore.setBuffetPlanLines([]);
        }

        const body = {
          table_id: tableId,
          cart_id,
          ticket_id: ticket_id,
          status_order: status_order,
          items: arr,
          price_channel_id: this.defineStore.selectedPriceChannelId || null,
        };
        if (
          this.isBuffet &&
          this.defineStore.buildBuffetPlansPayload().length > 0
        ) {
          body.buffet_plans = this.defineStore.buildBuffetPlansPayload();
        }
        const { data } = await this.$http.post("/make_table_order", body);
        if (data && data.status) {
          this.table_id = "";
          this.order_id = "";
          this.order_table = {};
          // clear cart
          this.defineStore.deleteCart();
          this.SET_PAYMENT();
          this.chooseTable = false;

          // Table selection is already preserved in the URL query parameters
          // No need to navigate since we're staying on the same route with the same query

          await toast(this, "add_order_success");

          // Close the drawer after successful order (mobile only)
          if (this.$vuetify.breakpoint.mobile) {
            this.drawer = false;
          }
        }
      } catch (error) {
        // Error handled silently
      }
    },

    async addQuotation() {
      try {
        const req_body = await Help.generateQuotation(
          this.newQuotation,
          this.defineStore.cart,
        );
        let api = "quotation_add";
        if (this.newQuotation.update) {
          api = `quotation_update`;
        }
        const { data } = await this.$http.post(api, req_body);
        if (data && data.status) {
          this.defineStore.deleteCart();
          this.SET_NEW_QUOTATION({});
          // confirm
          const { isConfirmed } = await confirm({
            props: this,
            text: "add_quotation_success",
            text: "want_to_print",
            con_txt: "print",
            can_txt: "done",
          });
          if (isConfirmed) {
            this.printBill = data.data;
            setTimeout(() => {
              this.$htmlToPaper("quotationPrint");
            }, 500);
          }
        }
      } catch (error) {
        // Error handled silently
      }
    },

    handleKeydown(event) {
      if (this.dialog) return;
      if (event.key === "F12") {
        event.preventDefault();
        if (this.store_type === "POS") {
          if (!this.ensureCanProcessSales()) return;
          this.dialogPayOpen();
        } else {
          if (!this.ensureCanProcessSales()) return;
          this.fetchTable();
        }
      }
      if (event.ctrlKey) {
        if (event.key === "d") {
          event.preventDefault();
          const { total_discount } = this.defineStore;
          this.amount = total_discount.toString();
          this.openDiscout();
        }
      }
    },

    openNoted(fromDrawer = false) {
      this.noteDraft = this.note;
      this.noteDialog = true;
    },

    saveNoteDraft() {
      this.note = this.noteDraft.trim();
      this.noteDialog = false;
    },

    clearNoteDraft() {
      this.note = "";
      this.noteDraft = "";
      this.noteDialog = false;
    },

    openDiscout() {
      console.log("openDiscout called");
      // Prevent opening if dialog is already open to avoid double modals
      if (this.dialog) {
        return;
      }
      this.dialog = true;
    },

    handleDiscountSaved(discountData) {
      console.log("handleDiscountSaved:", discountData);
      this.discountData = discountData;
      this.applyDiscount(discountData.amount);
      this.dialog = false;
    },

    handleDiscountApply() {
      console.log("handleDiscountApply called");
      if (this.$refs.discountModalRef) {
        const data = this.$refs.discountModalRef.getDiscountData();
        if (data) {
          this.handleDiscountSaved(data);
        }
      }
    },
    applyDiscount(amount) {
      // Calculate discount based on typeDiscount
      const discountValue = parseFloat(amount) || 0;
      let calculatedDiscount = 0;

      if (this.typeDiscount === "percentage") {
        // Calculate discount as percentage of total price
        const totalPrice = this.defineStore.total_price;
        calculatedDiscount = (discountValue / 100) * totalPrice;
      } else {
        // Use amount directly
        calculatedDiscount = discountValue;
      }

      // Apply discount to cart store
      this.defineStore.setDiscount(calculatedDiscount);
    },

    dialogQTY(item) {
      this.dialogQ = true;
      this.amountQty = item.qty.toString();
      this.cartItem = item;
    },
    async exchangeRate() {
      this.dlog("exchangeRate button clicked");
      await this.fetchActiveCurrencies(this);
      this.actionHandler = "exchangeRate";
      this.dlog("actionHandler ->", this.actionHandler);
    },

    getStoreName() {
      try {
        if (!this.user) return null;
        return (
          this.user?.stores?.store_id?.name ||
          this.user?.stores?.name ||
          this.user?.branch_id?.name ||
          null
        );
      } catch (e) {
        return null;
      }
    },
    submitExchangeRate(amount) {
      // Store the converted amount and currency data to apply after switching to payment view
      this.pendingConvertedAmount = amount;
      this.pendingCurrencyData = this.defineStore.currency;

      this.actionHandler = "payment";

      const newPayAmount = amount.toString();
      this.pay_amount = newPayAmount;
    },

    // New methods to handle SaleNumPad events
    handleSubmitPayment(paymentData) {
      if (paymentData.type === "qr") {
        this.status_payment = paymentData.status;
        this.submitPayment();
      }
    },

    handleManualPaymentSuccess(data) {
      // Show success message
      successPos(this, data);
      // Reset cart and close payment dialog
      this.defineStore.resetCart(this);
      this.qrCodeData = null;
      this.status_payment = "done";
      this.dialogPay = false;
      this.SET_PAYMENT();

      // Reset pay_amount
      this.pay_amount = "0";

      // Print receipt if confirmed
      if (data) {
        this.printBill = data;
        // Ensure component is still mounted before executing
        if (this.$el && this.$el.parentNode) {
          setTimeout(() => {
            if (this.$el && this.$el.parentNode) {
              this.$htmlToPaper("printBill");
            }
          }, 500);
        }
      }
    },

    handleDownloadQR(qrData) {
      // Handle QR download
      if (this.$refs.qrCard) {
        const code = qrData?.ticket_id || "ticket";
        this.$refs.qrCard.downloadAsImage(`QR-${code}.png`);
      }
    },

    handleSwitchToCash() {
      // Handle switching to cash payment
      this.paymentTab = "cash";
      // Reset QR state when switching to cash
      if (this.$refs.saleNumPad) {
        this.$refs.saleNumPad.resetQR();
      }
      // Reset payment status and amount
      this.status_payment = "done";
      this.pay_amount = "0";
      this.defineStore.setCurrency(null);
    },

    handlePaymentTabUpdate(newTab) {
      // Handle paymentTab updates from SaleNumPad component
      this.paymentTab = newTab;
    },

    handlePayAmountUpdate(newAmount) {
      // Handle pay_amount updates from SaleNumPad component
      this.pay_amount = newAmount;
    },

    handlePaymentAmountUpdated(data) {
      if (data && data.amount) {
        this.pay_amount = data.amount.toString();
      }
    },

    onCurrencyChange(currency) {
      try {
        this.dlog("currency-change event ->", currency);
        if (currency && currency.store_exchange_rate) {
          const rate = parseFloat(currency.store_exchange_rate);
          const total = this.finalTotalPrice;
          const converted = rate > 0 ? total / rate : total;

          this.dlog("computed conversion preview", { total, rate, converted });
        }

        this.defineStore.setCurrency(currency);

        // Only try to set currency on the UnifiedPaymentPad if the payment view is active
        if (this.actionHandler === "payment") {
          this.trySetExternalCurrency(currency, 0);
        }
      } catch (e) {
        // Error handled silently
      }
    },

    onNumpadClose() {
      this.dialog = false;
    },

    onNumpadSubmit(value) {
      this.amount = value;
    },

    handleDiscountApply(discountData) {
      // Apply discount using the discount data from DiscountModal
      this.typeDiscount = discountData.type;
      this.amount = discountData.value.toString();

      // Apply discount to cart store
      this.defineStore.setDiscount(discountData.amount);

      // Close dialog
      this.dialog = false;
    },
    // ================== NEW METHODS FROM POC ==================

    // Pause order - save cart as a held bill (make_cart_order)
    async pauseOrder() {
      if (this.defineStore.cart.length === 0) {
        return toast(this, "no_product", "error");
      }
      try {
        const { cart, txn: cartTxn } = await Help.cartArray(
          this.defineStore.cart,
        );
        const { data } = await this.$http.post("/make_cart_order", {
          customer_id: this.customer_id,
          txn: "ORD-" + Date.now(),
          data: cart,
        });
        if (data && data.status) {
          this.defineStore.deleteCart();
          this.SET_PAYMENT();
          await toast(this, "add_order_success");
        }
      } catch (error) {
        toast(this, "something_wrong", "error");
      }
    },

    // ================== HELD BILLS (ຖືບິນ) ==================

    // Open the held-bills modal and refresh the list from the backend.
    async openHeldBills() {
      this.heldOpen = true;
      await this.fetchHeldBills();
    },

    // Silent refresh of the held-bills list (used by the header badge too).
    async fetchHeldBills() {
      if (this.heldLoading) return;
      this.heldLoading = true;
      try {
        const { data } = await this.$http.get("/list_order");
        this.heldBills =
          data && data.status && Array.isArray(data.data) ? data.data : [];
      } catch (error) {
        console.log("fetchHeldBills ===> ", error);
        this.heldBills = [];
      } finally {
        this.heldLoading = false;
      }
    },

    // Held bill total from its lines, in the products' own currency.
    heldBillTotal(bill) {
      const lines = bill.orders || [];
      if (!lines.length) return Number(bill.total_price || 0);
      return lines.reduce(
        (sum, o) => sum + Number(o.product?.price_sale || 0) * Number(o.qty || 0),
        0,
      );
    },

    heldBillSymbol(bill) {
      const cur = (bill.orders || []).find((o) => o.product?.currency?.symbol)
        ?.product.currency;
      return cur ? cur.symbol : this.currencySymbolText;
    },

    heldTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    customerNameOf(c) {
      if (!c) return "";
      if (c.name) return c.name;
      if (c.en_name) return c.en_name;
      if (c.la_name) return c.la_name;
      const first = c.first_name || "";
      const last = c.last_name || "";
      return `${first} ${last}`.trim() || "";
    },

    // Load a held bill back into the cart (removes it from the held list).
    async resumeBill(bill) {
      try {
        const { data } = await this.$http.get(`/get_order/${bill.txn}`);
        if (data && data.status && data.data) {
          const cart = (data.data.orders || []).map((e) => ({
            txn: bill.txn,
            id: e.product_id,
            qty: e.qty,
            discount: 0,
            price: Number(e.product?.price_sale || 0) * e.qty,
            // keep the product's own currency (e.g. THB) so totals don't fall back to LAK
            currency: e.product?.currency,
            exchange_rate: e.product?.exchange_rate,
            item: e.product,
          }));
          this.defineStore.setCart(cart);
          this.heldBills = this.heldBills.filter((b) => b.txn !== bill.txn);
          this.heldOpen = false;
          await toast(this, "add_order_success");
        }
      } catch (error) {
        console.log("resumeBill ===> ", error);
      }
    },

    // Delete a held bill with confirmation.
    async deleteHeldBill(bill) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_remove",
        con_txt: "confirm",
        can_txt: "cancel",
      });
      if (!isConfirmed) return;
      try {
        const { data } = await this.$http.delete(`/delete_order/${bill.txn}`);
        if (data && data.status) {
          this.heldBills = this.heldBills.filter((b) => b.txn !== bill.txn);
          await toast(this, data.message || "success");
        }
      } catch (error) {
        console.log("deleteHeldBill ===> ", error);
      }
    },

    // Print receipt preview
    handlePrint() {
      // Show receipt preview before printing
      // Uses existing app-bill component functionality
      const { cart, total_price, total_discount } = this.defineStore;

      // Prepare print bill data in the format expected by app-bill
      this.printBill = {
        txn: "PREVIEW-" + Date.now(),
        orders: cart.map((item) => ({
          product_id: item.item.id,
          product: item.item,
          qty: item.qty,
          price: item.price,
          discount: 0,
        })),
        total_price: total_price,
        total_discount: total_discount,
        customer_id: this.customer_id,
      };

      // Show print preview
      this.print = true;

      // Hide after print dialog
      this.$nextTick(() => {
        setTimeout(() => {
          this.print = false;
        }, 1000);
      });
    },

    // Navigate to bill report
    navigateToBill() {
      this.$router.push({ name: "report_bill" });
    },

    // Navigate to documents (POS only)
    navigateToDocuments() {
      this.$router.push({ name: "documents" });
    },

    // Navigate to tables (Restaurant only)
    navigateToTables() {
      this.$router.push({ name: "table" });
    },

    // Navigate to products (Restaurant only)
    navigateToProducts() {
      this.$router.push({ name: "products" });
    },

    // Clear table selection from route
    clearTableSelection() {
      this.$router.push({
        query: {
          ...this.$route.query,
          tableId: null,
          tableName: null,
        },
      });
    },

    // Handle restaurant action (add order or choose table)
    handleRestaurantAction() {
      if (this.selectedTableFromRoute) {
        this.confirmTableOrder();
      } else {
        this.fetchTable();
      }
    },

    // Remove customer selection
    removeCustomer() {
      if (this.redeemStampPending) {
        this.defineStore.unmarkCartLineAsStampReward();
      }
      this.redeemStampPending = false;
      this.pickingStampReward = false;
      this.SET_CUSTOMER_ID(null);
    },

    // Clear cart with confirmation
    clearCart() {
      if (this.defineStore.cart.length === 0) return;
      this.clearCartDialog = true;
    },

    confirmClearCart() {
      this.defineStore.deleteCart();
      this.redeemStampPending = false;
      this.note = "";
      this.clearCartDialog = false;
    },

    // ================== END NEW METHODS FROM POC ==================

    // Retry mechanism for setting external currency
    trySetExternalCurrency(currency, retryCount) {
      const maxRetries = 5;
      const retryDelay = 100; // 100ms

      if (this.$refs.saleNumPad && this.$refs.saleNumPad.setExternalCurrency) {
        this.$refs.saleNumPad.setExternalCurrency(currency);

        // Clear currency pending data on success
        this.pendingCurrencyData = null;
      } else if (retryCount < maxRetries) {
        setTimeout(() => {
          this.trySetExternalCurrency(currency, retryCount + 1);
        }, retryDelay);
      }
    },

    // Retry mechanism for setting external payment amount
    trySetExternalPaymentAmount(amount, retryCount) {
      const maxRetries = 5;
      const retryDelay = 100; // 100ms

      if (
        this.$refs.saleNumPad &&
        this.$refs.saleNumPad.setExternalPaymentAmount
      ) {
        this.$refs.saleNumPad.setExternalPaymentAmount(amount);

        // Clear payment amount pending data on success
        this.pendingConvertedAmount = null;
      } else if (retryCount < maxRetries) {
        setTimeout(() => {
          this.trySetExternalPaymentAmount(amount, retryCount + 1);
        }, retryDelay);
      }
    },
  },

  beforeDestroy() {
    if (this._ticketPubNubListener) {
      try {
        this.pubnub.removeListener(this._ticketPubNubListener);
      } catch (error) {
        // Error handled silently
      }
      this._ticketPubNubListener = null;
    }

    if (this.currentTicketId) {
      try {
        this.pubnub.unsubscribe({
          channels: [String(this.currentTicketId)],
        });
      } catch (error) {
        // Error handled silently
      }
    }

    // Clear any pending timeouts
    if (this._timeoutIds) {
      this._timeoutIds.forEach((id) => clearTimeout(id));
    }
  },
};
</script>

<style scoped>
/* ============================================================
   Right cart panel — PosSmart v2 design (light panel)
   Brand: #5b39c9 · soft: #f3f0fc · ink: #141021
   ============================================================ */

/* Shared header bar (cart / table) */
.drawer-sale-header {
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0; /* same line as the sale page app bar */
  box-sizing: border-box;
  min-height: 65px; /* same total height as the app bar (65px incl. border) */
  display: flex;
  align-items: center;
}

.drawer-right-sale.theme--dark .drawer-sale-header {
  background-color: #171b21;
  border-bottom-color: #2f333a;
}

.drawer-sale-header__grow {
  min-width: 0;
}

.drawer-header-icon-btn {
  min-width: 40px !important;
  min-height: 40px !important;
}

/* ---- Cart header: icon box + title/subtitle | badge + clear ---- */
.drawer-cart-header__icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #fbf8f1;
  color: #5b39c9;
  border: 1px solid #141021;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 10px;
}

.drawer-cart-header__text {
  min-width: 0;
  line-height: 1;
}

.drawer-cart-header__title {
  font-size: 15px;
  font-weight: 500;
  color: #141021;
  line-height: 1.15;
  margin: 0;
}

.drawer-right-sale.theme--dark .drawer-cart-header__title {
  color: #e5e7eb;
}

.drawer-cart-header__sub {
  font-size: 11px;
  font-weight: 400;
  color: #94a3b8;
  line-height: 1;
  margin: 1px 0 0;
}

.drawer-cart-header__icon-btn {
  width: 32px !important;
  height: 32px !important;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  color: #94a3b8;
  margin-right: 8px;
  position: relative;
  /* also used for the non-interactive cart icon box (a span, not a v-btn) */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.drawer-cart-header__icon-btn:hover {
  color: #5b39c9 !important;
  border-color: #5b39c9 !important;
  background: #f3f0fc !important;
}

.drawer-cart-header__icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(91, 57, 201, 0.15);
  border: 1px solid rgba(91, 57, 201, 0.35);
  color: #5b39c9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 11px;
}

.drawer-cart-header__held-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  color: #5b39c9;
  background: rgba(91, 57, 201, 0.14);
  border: 1px solid rgba(91, 57, 201, 0.4);
  cursor: pointer;
}

.drawer-cart-header__clear {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  cursor: pointer;
  white-space: nowrap;
}

.drawer-cart-header__clear:hover {
  color: #dc2626;
  text-decoration: underline;
}

/* ---- Restaurant table header ---- */
.drawer-table-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}

/* ---- Customer bar ---- */
.drawer-customer-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f3f0fc;
  border-bottom: 1px solid #e2e8f0;
}

.drawer-customer-bar__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #5b39c9;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.drawer-customer-bar__body {
  min-width: 0;
  flex: 1;
}

.drawer-customer-bar__name {
  font-size: 12px;
  font-weight: 800;
  color: #141021;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-customer-bar__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.drawer-customer-bar__meta-item {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
}

.drawer-customer-bar__meta-item--primary {
  color: #5b39c9;
}

.drawer-customer-bar__meta-item--stamp {
  color: #b45309;
}

.drawer-customer-bar__close {
  width: 24px !important;
  height: 24px !important;
  color: #94a3b8 !important;
  flex-shrink: 0;
}

.drawer-customer-bar__close:hover {
  color: #dc2626 !important;
  background: #fff !important;
}

/* ---- Note bar ---- */
.drawer-note-bar {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 16px 10px;
  padding: 8px 11px;
  border-radius: 10px;
  background: rgba(91, 57, 201, 0.08);
  border: 1px solid rgba(91, 57, 201, 0.25);
}

.drawer-note-bar__icon {
  flex-shrink: 0;
  color: #5b39c9 !important;
}

.drawer-note-bar__body {
  min-width: 0;
  flex: 1;
}

.drawer-note-bar__label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
}

.drawer-note-bar__text {
  font-size: 12.5px;
  font-weight: 500;
  color: #5b39c9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-note-bar__clear {
  width: 22px !important;
  height: 22px !important;
  flex-shrink: 0;
}

/* ---- Drawer footer ---- */
.drawer-footer {
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.drawer-right-sale.theme--dark .drawer-footer {
  background: #171b21;
  border-top-color: #2f333a;
}

/* ---- Totals (PosSmart v2: plain rows + dashed divider + big brand total) ---- */
.drawer-totals {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.drawer-totals__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.drawer-totals__label {
  font-weight: 500;
  color: #64748b;
}

.drawer-totals__value {
  font-weight: 600;
  color: #334155;
}

.drawer-totals__divider {
  border-top: 1px dashed #e2e8f0;
  margin: 2px 0;
}

.drawer-totals__grand {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.drawer-totals__grand-label {
  font-size: 14px;
  font-weight: 800;
  color: #141021;
}

.drawer-totals__grand-value {
  font-size: 24px;
  font-weight: 500;
  color: #5b39c9;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

/* ---- Payment button: accent-tinted outline (matches the design reference) ----
   Structural sizing/typography lives on the plain .v-btn selector — Vuetify
   strips the "primary" class when the button is disabled, so anything scoped
   to `.v-btn.primary` (including font-size/weight) silently reverts to
   Vuetify's defaults in the disabled state otherwise. */
.drawer-pay-btn.v-btn {
  height: 52px !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.15s ease, opacity 0.15s ease;
}

.drawer-pay-btn.v-btn.primary {
  border: 1px solid #5b39c9 !important;
  background: rgba(91, 57, 201, 0.12) !important;
  /* higher specificity than Vuetify's .theme--light.v-btn.primary white text rule */
  color: #5b39c9 !important;
}

/* Vuetify's own generated stylesheet forces primary buttons' .v-btn__content
   and .v-icon to white via !important. .v-btn__content is Vuetify-internal
   markup with no scoped-CSS hash of ours, so a scoped descendant selector
   can never reach it — target our own label/icon elements directly instead. */
.drawer-pay-btn__label {
  color: #5b39c9 !important;
}

.drawer-pay-btn.v-btn.primary.theme--light:not(.v-btn--text):not(.v-btn--outlined)
  .v-icon {
  color: #5b39c9 !important;
}

.drawer-pay-btn.v-btn.v-btn--disabled {
  /* Vuetify strips the "primary" class when disabled, so the base rule's
     `border` shorthand no longer matches — restate it in full here, or a
     global `button { border-style: none }` reset wins and hides the border. */
  border: 1px solid #94a3b8 !important;
  background: transparent !important;
  color: #94a3b8 !important;
}

.drawer-pay-btn.v-btn.v-btn--disabled .drawer-pay-btn__label,
.drawer-pay-btn.v-btn.v-btn--disabled .v-icon {
  color: #94a3b8 !important;
}

.drawer-pay-btn.v-btn.primary:not(.v-btn--disabled):hover {
  background: rgba(91, 57, 201, 0.2) !important;
}

/* ---- Stamp redeem alert ---- */
.stamp-redeem-alert {
  border-radius: 12px;
}

/* ---- Note dialog textarea ---- */
.note-dialog-textarea ::v-deep .v-input__slot {
  background: #faf7ef !important;
  border-radius: 16px !important;
  box-shadow: none !important;
}

.note-dialog-textarea ::v-deep fieldset {
  border-radius: 12px !important;
  border-color: #141021 !important;
  border-width: 1px !important;
}

.note-dialog-textarea ::v-deep .v-input__slot {
  background: #fbf8f1 !important;
}

.note-dialog-textarea.v-textarea--focused ::v-deep .v-input__slot {
  background: #ffffff !important;
}

.note-dialog-textarea ::v-deep textarea {
  color: #141021;
  font-weight: 500;
}

/* ---- Discount dialog (brutal) ---- */
.ps-discount-dialog__panel {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  box-shadow: 5px 5px 0 0 #141021;
  padding: 24px 28px;
}

.ps-discount-dialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.ps-discount-dialog__title {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: #141021;
}

.ps-discount-dialog__close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.ps-discount-dialog__close:hover {
  background: #f1f5f9;
}

/* ---- Held bills dialog (brutal) ---- */
.ps-held-dialog__panel {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 24px;
  box-shadow: 5px 5px 0 0 #141021;
  padding: 24px 28px;
}

.ps-held-dialog__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ps-held-dialog__title {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: #141021;
}

.ps-held-dialog__sub {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
}

.ps-held-dialog__close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
  flex-shrink: 0;
}
.ps-held-dialog__close:hover {
  background: #f1f5f9;
}

.ps-held-dialog__list {
  margin-top: 20px;
  max-height: 60vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
}

.ps-held-dialog__item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
}

.ps-held-dialog__item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ps-held-dialog__item-label {
  margin: 0;
  font-weight: 900;
  font-size: 0.875rem;
  color: #141021;
}

.ps-held-dialog__item-time {
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
}

.ps-held-dialog__item-total {
  margin: 0;
  font-weight: 900;
  font-size: 0.875rem;
  color: #5b39c9;
  flex-shrink: 0;
}

.ps-held-dialog__item-meta {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.ps-held-dialog__item-count {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}

.ps-held-dialog__item-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 900;
  border-radius: 999px;
  padding: 2px 10px;
  border: 1px solid #e2e8f0;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ps-held-dialog__item-chip--brand {
  background: #f3f0fc;
  color: #5b39c9;
  border-color: rgba(91, 57, 201, 0.3);
}

.ps-held-dialog__item-actions {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.ps-held-dialog__btn {
  padding: 10px;
  border-radius: 12px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid #141021;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
}
.ps-held-dialog__btn--brand {
  background: #5b39c9;
  color: #fff;
  border: none;
  box-shadow: none;
}
.ps-held-dialog__btn--brand:hover {
  transform: none;
  box-shadow: none;
  background: #3b1e8c;
}
.ps-held-dialog__btn--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}
.ps-held-dialog__btn--danger:hover {
  background: #fee2e2;
}

.ps-held-dialog__empty {
  margin-top: 20px;
  padding: 40px 0;
  text-align: center;
}

.ps-held-dialog__empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f3f0fc;
  border: 2px dashed rgba(91, 57, 201, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(91, 57, 201, 0.5);
}

.ps-held-dialog__empty-title {
  margin: 12px 0 0;
  font-weight: 900;
  font-size: 0.875rem;
  color: #64748b;
}

/* Mobile drawer (300px) — keep long LAK totals on one line */
@media (max-width: 340px) {
  .drawer-totals__grand-value {
    font-size: 20px;
  }
  .drawer-pay-btn {
    font-size: 14px;
  }
}
</style>

<style>
/* ---- Brutal dialog shell for held-bills + discount dialogs ----
   Same as customDialog's `.v-dialog.custom-dialog-content` so these
   modals share the exact look: 24px radius, hard ink shadow, no Vuetify
   default soft elevation. Non-scoped because the class lands on the
   v-dialog element (content-class). */
.v-dialog.ps-held-dialog,
.v-dialog.ps-discount-dialog {
  border-radius: 24px !important;
  overflow: hidden !important;
  background: transparent !important;
  box-shadow: 5px 5px 0 0 #141021 !important;
}

@media (max-width: 600px) {
  .v-dialog.ps-held-dialog,
  .v-dialog.ps-discount-dialog {
    margin: 16px !important;
  }
}
</style>
